import React, { useCallback, useState, useMemo } from 'react'
import styled from 'styled-components'
import { Container } from '~/components/ui/Container'
import { MyBtn } from '~/components/ui/MyBtn'
import Link from 'next/link'
import { Dropdown } from 'antd'
import { observer } from 'mobx-react'
import { useRouter } from 'next/router'
import { MyImage } from '~/components/ui/MyImage'

import { useAuth } from '~/hooks/useAuth'
import { useValidateAuth } from '~/hooks/useValidateAuth'
import { UserModal } from '~/components/business/UserModal'
import { MessageEntry } from '~/components/pages/message/MessageEntry'
import { SearchType, ThemeType } from '~/graphqls/types'

import { useStores } from '~/hooks/useStores'
import { MySearch } from '~/components/ui/MySearch'

const StyledHeader = styled.div<{ isDarkMode: boolean }>`
  height: 80px;
  border-bottom: 1px solid
    ${props => (props.isDarkMode ? props.theme.dark3 : props.theme.light1)};
  margin-bottom: 40px;
  padding: 20px 0;
  color: ${props => props.theme.dark4};
  .header-main {
    display: flex;
    align-items: center;
  }
  .header-left {
    display: flex;
    flex: auto;
    align-items: center;
  }
  .header-divider {
    width: 2px;
    height: 40px;
    background-color: ${props =>
      props.isDarkMode ? props.theme.dark3 : props.theme.light1};
    margin: 0 16px 0 32px;
    display: flex;
  }
  .header-logo {
    height: 40px;
  }
  .header-nav {
    display: flex;
    align-items: center;
    list-style: none;
    padding: 0;
    margin: 0;
  }
  .header-nav-li {
    padding: 0 12px;
    position: relative;
    .new {
      position: absolute;
      right: 12px;
      top: -20px;
      img {
        height: 14px;
      }
    }
    &.active {
      a {
        color: ${props =>
          props.isDarkMode ? props.theme.light2 : props.theme.dark2};
      }
    }
    a {
      color: ${props => props.theme.dark4};
      &:hover {
        color: ${props =>
          props.isDarkMode ? props.theme.light2 : props.theme.dark2};
      }
    }
  }
  .header-right {
    display: flex;
    align-items: center;
  }

  .header-btn {
    margin-left: 12px;
    &.ant-btn-primary {
      border: 2px solid ${props => (props.isDarkMode ? props.theme.dark3 : '')};
      color: ${props => (props.isDarkMode ? props.theme.light3 : '')};
    }
    &.ant-btn-default {
      border: 2px solid
        ${props => (props.isDarkMode ? props.theme.dark3 : props.theme.light1)};
      color: ${props =>
        props.isDarkMode ? props.theme.light3 : props.theme.dark2};
    }
    &-user {
      border: 2px solid
        ${props => (props.isDarkMode ? props.theme.dark3 : props.theme.light1)};
      padding: 2px;
      display: flex;
      align-items: center;
      .user-avatar {
        border-radius: 50%;
        width: 32px;
        height: 32px;
      }
      span {
        margin: 0 6px 0 8px;
      }
    }
  }
`

const WUJIEAI_HOST = process.env.NEXT_PUBLIC_WUJIEAI_HOST

const navData = [
  {
    name: '首页',
    href: '/',
  },
  {
    name: '版权',
    href: '/market',
  },
  {
    name: '文创',
    href: '/mall',
  },
  {
    name: '创作者',
    href: '/creators',
  },
]

export const Header: React.FC = observer(() => {
  const { store } = useStores()
  const router = useRouter()
  const { isCreator, isAiCreator, isOrganization } = useAuth()
  const { validateIsRealName, validateBindBankCard } = useValidateAuth()
  const [userModalVisible, setUserModalVisible] = useState(false)

  const onUpload = useCallback(() => {
    if (validateIsRealName()) {
      if (isCreator || isAiCreator || isOrganization) {
        if (validateBindBankCard()) {
          router.push('/upload-product')
        }
      } else {
        router.push('/to-be-creator')
      }
    }
  }, [isCreator, isOrganization, validateBindBankCard, validateIsRealName])

  const onLogin = useCallback(() => {
    store.modal?.setLoginVisible(true)
  }, [store.modal])

  const inMall = useMemo(() => {
    return router.pathname.indexOf('/mall') > -1
  }, [router])
  const inSearch = useMemo(() => {
    return router.pathname.indexOf('/search') > -1
  }, [router])
  const inCollection = useMemo(() => {
    return router.pathname.indexOf('/collections') > -1
  }, [router])
  const inCreators = useMemo(() => {
    return router.pathname.indexOf('/creators') > -1
  }, [router])

  const isDarkMode = useMemo(() => {
    return store.themeMode === ThemeType.DARK
  }, [store.themeMode])

  const onSearch = useCallback(
    value => {
      if (!value?.trim()) return

      if (inMall) {
        router.push({
          pathname: '/search',
          query: {
            keyword: value?.trim(),
            type: SearchType.MALL,
          },
        })
      } else if (inCollection) {
        router.push({
          pathname: '/search',
          query: {
            keyword: value?.trim(),
            type: SearchType.COLLECTION,
          },
        })
      } else if (inCreators) {
        router.push({
          pathname: '/search',
          query: {
            keyword: value?.trim(),
            type: SearchType.USER,
          },
        })
      } else if (inSearch) {
        router.push({
          pathname: '/search',
          query: {
            ...router.query,
            keyword: value?.trim(),
          },
        })
      } else {
        router.push({
          pathname: '/search',
          query: {
            keyword: value?.trim(),
            type: SearchType.ARTWORK,
          },
        })
      }
    },
    [router, inMall, inSearch, inCollection, inCreators]
  )

  const closeUserModal = () => setUserModalVisible(false)

  return (
    <StyledHeader isDarkMode={isDarkMode}>
      <Container>
        <div className="header-main">
          <div className="header-left">
            <Link href="/">
              <a>
                <img
                  className="header-logo"
                  src={
                    isDarkMode
                      ? '/images/logo-v2-dark.svg'
                      : '/images/logo-v2.svg'
                  }
                  alt=""
                />
              </a>
            </Link>
            <span className="header-divider"></span>
            <ul className="header-nav">
              {navData.map(nav => (
                <li
                  className={
                    router.pathname === nav.href
                      ? 'header-nav-li active'
                      : 'header-nav-li'
                  }
                  key={nav.href}
                >
                  {nav.href.startsWith('http') ? (
                    <a
                      className="flex items-center"
                      target="_blank"
                      href={nav.href}
                      rel="noreferrer"
                    >
                      <span>{nav.name}</span>
                    </a>
                  ) : (
                    <Link href={nav.href}>
                      <a className="flex items-center">
                        <span>{nav.name}</span>
                      </a>
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div className="header-right">
            <MySearch
              $dark={isDarkMode}
              placeholder="搜索你想要的"
              defaultValue={router.query.keyword as string}
              style={{ marginRight: 16 }}
              onSearch={onSearch}
              maxLength={40}
              allowClear
            />
            {store.token ? (
              <>
                <MessageEntry isDark={isDarkMode} />
                <MyBtn type="primary" className="header-btn" onClick={onUpload}>
                  {isCreator || isAiCreator || isOrganization
                    ? '上传作品'
                    : '申请入驻'}
                </MyBtn>
                <Dropdown
                  placement="bottomRight"
                  overlay={
                    <UserModal
                      close={closeUserModal}
                      visible={userModalVisible}
                    />
                  }
                  trigger={['click']}
                  visible={userModalVisible}
                  onVisibleChange={visible => setUserModalVisible(visible)}
                >
                  <MyBtn className="header-btn header-btn-user">
                    <MyImage
                      tag="v400"
                      className="user-avatar"
                      lazy={false}
                      src={store.userinfo?.avatarUrl}
                    />
                  </MyBtn>
                </Dropdown>
              </>
            ) : (
              <>
                <MyBtn className="header-btn" onClick={onLogin}>
                  登录/注册
                </MyBtn>
              </>
            )}
          </div>
        </div>
      </Container>
    </StyledHeader>
  )
})
